import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Moon, Monitor, Search, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { primaryRoleLabel, roleScopeLabel } from "@/lib/roles";
import { ADMIN_ROUTES, NAV_GROUPS, canOpen, type AdminRoute, type NavGroupId } from "@/navigation/registry";
import { useActiveRoute } from "@/navigation/useActiveRoute";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useThemePreference } from "@/hooks/use-theme-preference";
import BrandMark from "@/components/brand/BrandMark";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type SidebarProps = {
  collapsed?: boolean;
  /** Opens the ⌘K palette. */
  onOpenSearch?: () => void;
  /** Called after a link is followed (closes the mobile sheet). */
  onNavigate?: () => void;
};

const OPEN_GROUPS_KEY = "admin-nav-open-groups";

const readOpenGroups = (): NavGroupId[] => {
  try {
    const raw = sessionStorage.getItem(OPEN_GROUPS_KEY);
    return raw ? (JSON.parse(raw) as NavGroupId[]) : [];
  } catch {
    return [];
  }
};

const Badge = ({ value, active }: { value?: number; active?: boolean }) =>
  value ? (
    <span
      className={cn(
        "ml-auto min-w-[22px] h-[22px] px-2 rounded-full text-xs font-bold leading-[22px] text-center",
        active ? "bg-primary text-primary-foreground" : "bg-background text-ink-2"
      )}
      aria-label={`${value} waiting`}
    >
      {value > 999 ? "999+" : value}
    </span>
  ) : null;

const Sidebar = ({ collapsed = false, onOpenSearch, onNavigate }: SidebarProps) => {
  const { user } = useAuth();
  const roleIds = useMemo(() => user?.roleIds ?? [], [user?.roleIds]);
  const { navRoute, group: activeGroup } = useActiveRoute();
  const badges = useNavBadges(!!user);
  const { pref, cycle } = useThemePreference();

  // Only links this person can open. Groups with nothing left disappear.
  const groups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: ADMIN_ROUTES.filter((r) => r.nav?.group === g.id && canOpen(r, roleIds)),
      })).filter((g) => g.items.length > 0),
    [roleIds]
  );

  const [openGroups, setOpenGroups] = useState<NavGroupId[]>(readOpenGroups);

  // The group holding the current page is always open, including right after a ⌘K jump.
  useEffect(() => {
    if (activeGroup && activeGroup.id !== "today" && !openGroups.includes(activeGroup.id)) {
      setOpenGroups((prev) => [...prev, activeGroup.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroup?.id]);

  useEffect(() => {
    try {
      sessionStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
    } catch {
      /* ignore */
    }
  }, [openGroups]);

  const toggleGroup = (id: NavGroupId) =>
    setOpenGroups((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  const ThemeIcon = pref === "light" ? Sun : pref === "dark" ? Moon : Monitor;
  const themeLabel = `Theme: ${pref}. Switch theme`;

  const linkClass = (active: boolean, nested = false) =>
    cn(
      "flex items-center gap-3 rounded-[10px] text-sm leading-5 transition-colors outline-none",
      "focus-visible:ring-2 focus-visible:ring-ring",
      nested ? "h-9 pl-[42px] pr-3" : "h-10 px-3",
      active
        ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold"
        : "text-sidebar-foreground/90 hover:bg-background/70 font-medium"
    );

  const renderItem = (item: AdminRoute) => {
    const active = navRoute?.path === item.path;
    const badge = item.nav?.badge ? badges[item.nav.badge] : undefined;
    return (
      <li key={item.path}>
        <Link
          to={item.path}
          onClick={onNavigate}
          aria-current={active ? "page" : undefined}
          className={linkClass(active, true)}
        >
          <span className="truncate">{item.title}</span>
          <Badge value={badge} active={active} />
        </Link>
      </li>
    );
  };

  // ------------------------------------------------------------- collapsed
  if (collapsed) {
    return (
      <aside
        aria-label="Admin navigation"
        className="w-[72px] shrink-0 h-full flex flex-col items-center bg-sidebar border-r border-sidebar-border py-3 gap-2"
      >
        <div className="h-12 flex items-center">
          <BrandMark compact size={30} />
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onOpenSearch}
              aria-label="Search or jump to (⌘K)"
              className="h-10 w-10 rounded-[10px] border bg-card flex items-center justify-center text-ink-3 hover:text-foreground"
            >
              <Search className="h-[18px] w-[18px]" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">Search or jump to… ⌘K</TooltipContent>
        </Tooltip>
        <nav aria-label="Admin" className="flex-1 flex flex-col gap-1 mt-2">
          {groups.map((g) => {
            const Icon = g.icon;
            const isActive = activeGroup?.id === g.id;
            if (g.id === "today") {
              const today = g.items[0];
              return (
                <Tooltip key={g.id}>
                  <TooltipTrigger asChild>
                    <Link
                      to={today.path}
                      aria-label={g.label}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "h-10 w-10 rounded-[10px] flex items-center justify-center",
                        isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "hover:bg-background/70"
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{g.label}</TooltipContent>
                </Tooltip>
              );
            }
            return (
              <DropdownMenu key={g.id}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={g.label}
                    className={cn(
                      "h-10 w-10 rounded-[10px] flex items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isActive ? "bg-sidebar-accent text-sidebar-accent-foreground" : "hover:bg-background/70"
                    )}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="start" className="w-60 max-h-[70vh] overflow-y-auto">
                  <DropdownMenuLabel>{g.label}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {g.items.map((item) => (
                    <DropdownMenuItem key={item.path} asChild>
                      <Link to={item.path} className={cn(navRoute?.path === item.path && "font-semibold text-blue-ink")}>
                        {item.title}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </nav>
        <button
          type="button"
          onClick={cycle}
          aria-label={themeLabel}
          title={themeLabel}
          className="h-10 w-10 rounded-[10px] flex items-center justify-center text-ink-3 hover:bg-background/70"
        >
          <ThemeIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </aside>
    );
  }

  // -------------------------------------------------------------- expanded
  return (
    <aside
      aria-label="Admin navigation"
      className="w-[248px] shrink-0 h-full flex flex-col bg-sidebar border-r border-sidebar-border px-3 pb-4"
    >
      <div className="h-16 flex items-center px-1.5 shrink-0">
        <BrandMark showAdminTag />
      </div>

      <button
        type="button"
        onClick={onOpenSearch}
        className="h-10 shrink-0 rounded-[10px] border bg-card px-2.5 mb-4 flex items-center gap-2 text-ink-3 text-sm hover:border-ink-3/40 outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Search className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Search or jump to…</span>
        <kbd className="text-xs font-semibold border rounded-md px-1.5 py-px">⌘K</kbd>
      </button>

      <nav aria-label="Admin" className="flex-1 overflow-y-auto -mx-1 px-1">
        <ul className="flex flex-col gap-1">
          {groups.map((g) => {
            const Icon = g.icon;
            if (g.id === "today") {
              const today = g.items[0];
              const active = navRoute?.path === today.path;
              return (
                <li key={g.id}>
                  <Link
                    to={today.path}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className={linkClass(active)}
                  >
                    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>{g.label}</span>
                  </Link>
                </li>
              );
            }

            const open = openGroups.includes(g.id);
            const holdsActive = activeGroup?.id === g.id;
            const groupBadge = g.items.reduce((sum, i) => sum + (i.nav?.badge ? badges[i.nav.badge] ?? 0 : 0), 0);
            let lastSection: string | undefined;

            return (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => toggleGroup(g.id)}
                  aria-expanded={open}
                  aria-controls={`nav-group-${g.id}`}
                  className={cn(
                    "w-full h-10 px-3 rounded-[10px] flex items-center gap-3 text-sm leading-5 outline-none",
                    "focus-visible:ring-2 focus-visible:ring-ring hover:bg-background/70",
                    holdsActive ? "text-foreground font-semibold" : "text-sidebar-foreground/90 font-medium"
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <span className="flex-1 text-left">{g.label}</span>
                  {!open && <Badge value={groupBadge} />}
                  <ChevronDown
                    className={cn("h-4 w-4 text-ink-3 transition-transform", open ? "rotate-0" : "-rotate-90")}
                    aria-hidden="true"
                  />
                </button>
                {open && (
                  <ul id={`nav-group-${g.id}`} className="mt-0.5 mb-1 flex flex-col gap-0.5">
                    {g.items.map((item) => {
                      const section = item.nav?.section;
                      const heading =
                        section && section !== lastSection ? (
                          <li
                            key={`h-${section}`}
                            aria-hidden="true"
                            className="pl-[42px] pt-2 pb-1 text-[12px] leading-4 font-semibold uppercase tracking-[0.8px] text-ink-3"
                          >
                            {section}
                          </li>
                        ) : null;
                      lastSection = section;
                      return [heading, renderItem(item)];
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="shrink-0 mt-3 pt-3 border-t border-sidebar-border flex items-center gap-2.5 px-1">
        <div
          aria-hidden="true"
          className="h-9 w-9 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold"
        >
          {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{user?.name || user?.email}</div>
          <div className="text-xs text-ink-3 truncate">
            {primaryRoleLabel(user)} · {roleScopeLabel(user)}
          </div>
        </div>
        <button
          type="button"
          onClick={cycle}
          aria-label={themeLabel}
          title={themeLabel}
          className="h-9 w-9 shrink-0 rounded-[10px] flex items-center justify-center text-ink-3 hover:bg-background/70 outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ThemeIcon className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
